import { useMemo, useState } from 'react';
import Segmented from '@/components/base/Segmented';
import SettingsBreadcrumb from '@/pages/settings/components/SettingsBreadcrumb';
import RetentionCard from '@/pages/settings/logs/components/RetentionCard';
import { useActivityLogs, type LogFilters } from '@/hooks/useActivityLogs';
import { useActivityLogRetention } from '@/hooks/useActivityLogRetention';
import { formatDateTime } from '@/lib/format';
import { resolveLevelKey, type LogLevelKey } from '@/lib/logLevels';

const LEVEL_META: Record<
  LogLevelKey,
  { label: string; badge: string; icon: string }
> = {
  info: {
    label: 'Información',
    badge: 'bg-background-100 text-foreground-700',
    icon: 'ri-information-line',
  },
  warning: {
    label: 'Aviso',
    badge: 'bg-accent-100 text-accent-900',
    icon: 'ri-alert-line',
  },
  error: {
    label: 'Error',
    badge: 'bg-secondary-100 text-secondary-900',
    icon: 'ri-error-warning-line',
  },
  critical: {
    label: 'Crítico',
    badge: 'bg-foreground-950 text-background-50',
    icon: 'ri-alarm-warning-line',
  },
};

const RESULT_META: Record<string, { label: string; className: string }> = {
  success: { label: 'Éxito', className: 'text-accent-700' },
  failure: { label: 'Fallo', className: 'text-primary-700' },
  warning: { label: 'Aviso', className: 'text-secondary-700' },
  unknown: { label: '—', className: 'text-foreground-500' },
};

function levelMeta(level: string) {
  return LEVEL_META[resolveLevelKey(level)];
}

const MODULE_LABELS: Record<string, string> = {
  auth: 'Acceso',
  products: 'Productos',
  inventory: 'Inventario',
  pos: 'POS',
  sales: 'Ventas',
  cash: 'Caja',
  offers: 'Ofertas',
  discounts: 'Descuentos',
  settings: 'Configuración',
  branches: 'Sucursales',
  users: 'Usuarios',
  catalog: 'Catálogo',
  images: 'Imágenes',
  app: 'Aplicación',
  general: 'General',
};

function moduleLabel(mod: string) {
  return MODULE_LABELS[mod] ?? mod;
}

export default function ActivityLogsPage() {
  const [tab, setTab] = useState<'activity' | 'errors'>('activity');
  const [level, setLevel] = useState<string | null>(null);
  const [module, setModule] = useState<string | null>(null);
  const [result, setResult] = useState<string | null>(null);
  const [search, setSearch] = useState('');
  const [rangeDays, setRangeDays] = useState(7);

  const filters = useMemo<LogFilters>(
    () => ({
      level,
      module,
      result,
      search,
      errorsOnly: tab === 'errors',
      rangeDays,
    }),
    [level, module, result, search, tab, rangeDays]
  );

  const { rows, loading, loadingMore, error, hasMore, loadMore, modules, counts, reload } =
    useActivityLogs(filters);

  const retention = useActivityLogRetention(true, reload);

  const selectClass =
    'rounded-md border border-background-300 bg-background-50 px-3 py-2 text-sm text-foreground-900 focus:outline-none focus:ring-2 focus:ring-primary-400 cursor-pointer';

  return (
    <div>
      <SettingsBreadcrumb label="Seguridad y actividad" icon="ri-shield-check-line" />

      <div className="mt-4 flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4">
        <div>
          <h1 className="font-heading text-2xl md:text-3xl font-bold text-foreground-950">
            Seguridad y actividad
          </h1>
          <p className="mt-1 text-foreground-600 max-w-2xl">
            Registro centralizado de acciones del sistema y errores. Solo visible para
            administradores.
          </p>
        </div>
        <button
          onClick={reload}
          className="inline-flex items-center justify-center gap-2 rounded-md border border-background-300 px-4 py-2.5 text-sm font-medium text-foreground-700 hover:bg-background-100 whitespace-nowrap transition cursor-pointer"
        >
          <i className="ri-refresh-line" /> Actualizar
        </button>
      </div>

      {/* Resumen */}
      <div className="mt-6 grid grid-cols-1 sm:grid-cols-3 gap-4">
        <div className="rounded-lg border border-background-200 bg-background-50 p-5">
          <p className="text-sm text-foreground-600">Actividad de hoy</p>
          <p className="mt-1 font-heading text-3xl font-bold text-foreground-950">{counts.today}</p>
        </div>
        <div className="rounded-lg border border-background-200 bg-background-50 p-5">
          <p className="text-sm text-foreground-600">Errores (período)</p>
          <p className="mt-1 font-heading text-3xl font-bold text-secondary-800">{counts.errors}</p>
        </div>
        <div className="rounded-lg border border-background-200 bg-background-50 p-5">
          <p className="text-sm text-foreground-600">Críticos (período)</p>
          <p className="mt-1 font-heading text-3xl font-bold text-foreground-950">{counts.critical}</p>
        </div>
      </div>

      <RetentionCard
        settings={retention.settings}
        purging={retention.purging}
        message={retention.message}
        error={retention.error}
        onUpdate={retention.update}
        onPurge={retention.runPurge}
        onDismiss={retention.dismiss}
      />

      {/* Pestañas y filtros */}
      <div className="mt-6 flex flex-col gap-4">
        <Segmented
          options={[
            { value: 'activity', label: 'Toda la actividad' },
            { value: 'errors', label: 'Errores' },
          ]}
          value={tab}
          onChange={(v) => setTab(v as 'activity' | 'errors')}
        />

        <div className="flex flex-wrap items-center gap-2">
          {tab === 'activity' && (
            <select
              value={level ?? ''}
              onChange={(e) => setLevel(e.target.value || null)}
              className={selectClass}
              aria-label="Nivel"
            >
              <option value="">Todos los niveles</option>
              <option value="info">Información</option>
              <option value="warning">Aviso</option>
              <option value="error">Error</option>
              <option value="critical">Crítico</option>
            </select>
          )}
          <select
            value={module ?? ''}
            onChange={(e) => setModule(e.target.value || null)}
            className={selectClass}
            aria-label="Módulo"
          >
            <option value="">Todos los módulos</option>
            {modules.map((m) => (
              <option key={m} value={m}>
                {moduleLabel(m)}
              </option>
            ))}
          </select>
          <select
            value={result ?? ''}
            onChange={(e) => setResult(e.target.value || null)}
            className={selectClass}
            aria-label="Resultado"
          >
            <option value="">Todos los resultados</option>
            <option value="success">Éxito</option>
            <option value="failure">Fallo</option>
            <option value="warning">Aviso</option>
          </select>
          <select
            value={rangeDays}
            onChange={(e) => setRangeDays(Number(e.target.value))}
            className={selectClass}
            aria-label="Período"
          >
            <option value={1}>Últimas 24 h</option>
            <option value={7}>Últimos 7 días</option>
            <option value={30}>Últimos 30 días</option>
            <option value={90}>Últimos 90 días</option>
          </select>
          <div className="relative flex-1 min-w-[220px]">
            <i className="ri-search-line absolute left-3 top-1/2 -translate-y-1/2 text-foreground-400 text-sm" />
            <input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Buscar por acción, usuario o referencia…"
              className="w-full rounded-md border border-background-300 bg-background-50 pl-9 pr-3 py-2 text-sm text-foreground-900 focus:outline-none focus:ring-2 focus:ring-primary-400 placeholder:text-foreground-400"
            />
          </div>
        </div>

        {error && (
          <div className="rounded-lg border border-accent-200 bg-accent-50 px-4 py-3 flex items-center gap-3">
            <i className="ri-error-warning-line text-accent-700" />
            <p className="text-sm text-foreground-700">{error}</p>
            <button
              onClick={reload}
              className="ml-auto text-sm font-medium text-primary-700 hover:underline cursor-pointer whitespace-nowrap"
            >
              Reintentar
            </button>
          </div>
        )}

        {loading ? (
          <div className="flex items-center justify-center py-16">
            <div className="w-10 h-10 rounded-full border-2 border-primary-500 border-t-transparent animate-spin" />
          </div>
        ) : rows.length === 0 ? (
          <div className="rounded-lg border border-dashed border-background-300 p-12 text-center">
            <i className="ri-file-list-3-line text-4xl text-foreground-400" />
            <p className="mt-3 text-foreground-600">No hay registros para estos filtros.</p>
          </div>
        ) : (
          <>
            <div className="rounded-lg border border-background-200 bg-background-50 overflow-hidden divide-y divide-background-100">
              {rows.map((row) => {
                const meta = levelMeta(row.level);
                const res = RESULT_META[row.result] ?? RESULT_META.unknown;
                return (
                  <div key={row.id} className="px-4 md:px-5 py-4">
                    <div className="flex flex-wrap items-center gap-2">
                      <span
                        className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-xs font-semibold ${meta.badge}`}
                      >
                        <i className={meta.icon} /> {meta.label}
                      </span>
                      <span className="rounded-full bg-background-100 text-foreground-700 px-2 py-0.5 text-xs font-medium">
                        {moduleLabel(row.module)}
                      </span>
                      <span className="text-sm font-semibold text-foreground-950">{row.action}</span>
                      <span className={`text-xs font-medium ${res.className}`}>{res.label}</span>
                      <span className="ml-auto text-xs text-foreground-500 whitespace-nowrap">
                        {formatDateTime(row.created_at)}
                      </span>
                    </div>
                    {row.description && (
                      <p className="mt-1.5 text-sm text-foreground-700">{row.description}</p>
                    )}
                    <div className="mt-2 flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-foreground-500">
                      {row.user_name && (
                        <span className="inline-flex items-center gap-1">
                          <i className="ri-user-3-line" /> {row.user_name}
                          {row.role ? ` · ${row.role}` : ''}
                        </span>
                      )}
                      {row.target_type && (
                        <span className="inline-flex items-center gap-1">
                          <i className="ri-price-tag-3-line" /> {row.target_type}
                          {row.target_id ? `: ${row.target_id}` : ''}
                        </span>
                      )}
                      {row.ip && (
                        <span className="inline-flex items-center gap-1">
                          <i className="ri-global-line" /> {row.ip}
                        </span>
                      )}
                    </div>
                    {row.metadata && Object.keys(row.metadata).length > 0 && (
                      <pre className="mt-2 rounded-md bg-background-100 px-3 py-2 text-xs text-foreground-600 overflow-x-auto">
                        {JSON.stringify(row.metadata)}
                      </pre>
                    )}
                  </div>
                );
              })}
            </div>

            {hasMore && (
              <div className="flex justify-center">
                <button
                  onClick={loadMore}
                  disabled={loadingMore}
                  className="inline-flex items-center gap-2 rounded-full border border-background-300 px-5 py-2.5 text-sm font-medium text-foreground-700 hover:bg-background-100 whitespace-nowrap transition disabled:opacity-60 cursor-pointer"
                >
                  {loadingMore ? (
                    <>
                      <span className="w-4 h-4 rounded-full border-2 border-primary-500 border-t-transparent animate-spin" />
                      Cargando...
                    </>
                  ) : (
                    <>
                      <i className="ri-arrow-down-line" /> Ver más
                    </>
                  )}
                </button>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}